import React, {useState} from 'react';
import Form from "../../Form";
import ProductMultiselect from "../ProductMultiselect";

const TYPE_LABELS = {
    channel: 'Storewide',
    product: 'Specific Product(s)',
    free_shipping: 'Free Shipping',
    bundle: 'Bundle Discount',
};

function CouponForm({
                        initialValues = {},
                        onSubmit,
                        onChange,
                        submitBtnText = 'Create Coupon',
}) {
    const [values, setValues] = useState({
        ...initialValues,
        dollarAmount: initialValues.dollarAmount ? (initialValues.dollarAmount / 100).toFixed(2) : '0',
        percentageAmount: initialValues.percentageAmount ? initialValues.percentageAmount.toString() : '0',
        bundleMinItems: initialValues.metadata?.bundleMinItems || 2,
        isCombinable: initialValues.isCombinable === false ? 'false' : 'true',
    });
    const [errors, setErrors] = useState({});

    const handleChange = (newValues) => {
        setValues(newValues);
        if (onChange) onChange(newValues);
        setErrors({});
    };

    const handleSubmit = (formValues) => {
        const newErrors = {};
        if (!formValues.type) newErrors.type = 'Coupon type is required';
        if (!formValues.code) newErrors.code = 'Coupon code is required';

        const isFreeShipping = formValues.type === 'free_shipping';
        const isBundle = formValues.type === 'bundle';

        if (!isFreeShipping) {
            if (!formValues.dollarAmount && !formValues.percentageAmount) {
                newErrors.dollarAmount = 'At least one discount amount is required';
                newErrors.percentageAmount = 'At least one discount amount is required';
            }
            if (formValues.dollarAmount && formValues.dollarAmount < 0) {
                newErrors.dollarAmount = 'Discount amount must be non-negative';
            }
            if (formValues.percentageAmount && (formValues.percentageAmount < 0 || formValues.percentageAmount > 100)) {
                newErrors.percentageAmount = 'Discount percentage must be between 0 and 100';
            }
        }

        if (isBundle) {
            const min = parseInt(formValues.bundleMinItems, 10);
            if (!min || min < 2) newErrors.bundleMinItems = 'Bundle requires at least 2 items';
        }

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const submitValues = {
            ...formValues,
            isCombinable: formValues.isCombinable === true || formValues.isCombinable === 'true',
        };
        if (isBundle) {
            submitValues.metadata = {
                ...(formValues.metadata || {}),
                bundleMinItems: parseInt(formValues.bundleMinItems, 10) || 2,
            };
        }

        if (onSubmit) onSubmit(submitValues);
    };

    const type = values.type;
    const isFreeShipping = type === 'free_shipping';
    const isBundle = type === 'bundle';
    const showProductSelector = type === 'product' || type === 'free_shipping' || type === 'bundle';

    const baseFields = [
        {
            type: 'select',
            name: 'type',
            label: 'Coupon Type',
            options: Object.entries(TYPE_LABELS).map(([value, label]) => ({ value, label })),
            required: true,
            groupStyle: {flexBasis: '100%'},
        },
        {
            type: 'text',
            name: 'code',
            label: 'Coupon Code',
            placeholder: 'Enter a unique coupon code',
            required: true,
        },
        {
            type: 'select',
            name: 'isCombinable',
            label: 'Combinable with Other Coupons',
            options: [
                { value: 'true', label: 'Yes' },
                { value: 'false', label: 'No' }
            ],
        },
        {
            type: 'date',
            name: 'expiresAt',
            label: 'Expiration Date',
            placeholder: 'Select an expiration date',
        },
        {
            type: 'number',
            name: 'maxRedemptions',
            label: 'Max Redemptions',
            placeholder: 'How many times can this be used?',
            min: 0,
            step: 1,
            required: false,
            help: "Leave blank or 0 for unlimited."
        },
    ];

    const discountFields = isFreeShipping ? [] : [
        {
            type: 'number',
            name: 'dollarAmount',
            label: 'Discount Amount ($)',
            placeholder: 'Enter discount amount in dollars',
            min: 0,
            step: 0.01,
            required: !isFreeShipping,
            help: "Combines with percentage."
        },
        {
            type: 'number',
            name: 'percentageAmount',
            label: 'Discount Amount (%)',
            placeholder: 'Enter discount amount as a percentage',
            min: 0,
            max: 100,
            step: 1,
            required: !isFreeShipping,
            help: "Combines with dollar amount."
        },
    ];

    const bundleFields = isBundle ? [
        {
            type: 'number',
            name: 'bundleMinItems',
            label: 'Minimum Items in Bundle',
            placeholder: 'e.g. 3',
            min: 2,
            step: 1,
            required: true,
            help: "Shopper must have at least this many qualifying items for the discount to apply.",
            groupStyle: {flexBasis: '100%'},
        },
    ] : [];

    const fields = [...baseFields, ...discountFields, ...bundleFields];

    const ProductSelector = showProductSelector ? (
        <div className="group" style={{flexBasis: '100%'}}>
            <label>
                {type === 'free_shipping' ? 'Limit to Specific Products (optional)' :
                 type === 'bundle' ? 'Qualifying Products (optional — leave blank for all)' :
                 'Applicable Products'}
            </label>
            <ProductMultiselect
                values={values.productIds || []}
                onChange={(updatedProductIds) => handleChange({...values, productIds: updatedProductIds})}
            />
        </div>
    ) : null;

    return (
        <Form
            fields={fields}
            className="create-coupon-form flexcol twocol"
            values={values}
            errors={errors}
            onSubmit={handleSubmit}
            onChange={handleChange}
        >
            {ProductSelector}
            <div className="group">
                <input className="button primary" type="submit" value={submitBtnText || 'Create Coupon'} />
            </div>
        </Form>
    );
}

export default CouponForm;
